import * as Sentry from '@sentry/react';
import { type User, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { auth } from '@/lib/firebase';

import type { CustomClaims } from '@shared/types/auth';
import type { JSX } from 'react';

export type AuthState = {
  user: User | null;
  claims: CustomClaims | null;
  loading: boolean;
  signin: (email: string, password: string) => Promise<void>;
  signout: () => Promise<void>;
  refreshClaims: () => Promise<void>;
};

export const AuthContext = createContext<AuthState | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
};

function extractClaims(fbUser: User, forceRefresh: boolean): Promise<CustomClaims> {
  return fbUser.getIdTokenResult(forceRefresh).then((result) => {
    // `result.claims` is typed as `ParsedToken` (string-indexed `any`). Read-only at
    // this boundary — claims are authoritative because they're signed by Google.
    return result.claims as unknown as CustomClaims;
  });
}

function applySentryUser(fbUser: User | null): void {
  if (fbUser === null) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser(fbUser.email ? { id: fbUser.uid, email: fbUser.email } : { id: fbUser.uid });
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      userRef.current = fbUser;
      if (fbUser === null) {
        setUser(null);
        setClaims(null);
        applySentryUser(null);
        setLoading(false);
        return;
      }

      void extractClaims(fbUser, false)
        .then((parsedClaims) => {
          setUser(fbUser);
          setClaims(parsedClaims);
          applySentryUser(fbUser);
          return undefined;
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          setUser(fbUser);
          setClaims(null);
          applySentryUser(fbUser);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    return () => {
      unsub();
    };
  }, []);

  const signin = useCallback(async (email: string, password: string): Promise<void> => {
    // Claims will be loaded by the onAuthStateChanged listener.
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signout = useCallback(async (): Promise<void> => {
    await signOut(auth);
  }, []);

  const refreshClaims = useCallback(async (): Promise<void> => {
    const current = userRef.current;
    if (current === null) return;
    const parsedClaims = await extractClaims(current, true);
    setClaims(parsedClaims);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, claims, loading, signin, signout, refreshClaims }),
    [user, claims, loading, signin, signout, refreshClaims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
